import React, { useState, useRef } from "react";
import { useStyletron } from "baseui";
import axios from "axios";
import { Grid, Cell } from "baseui/layout-grid";

import { Card, StyledBody } from "baseui/card";
import { Button } from "baseui/button";
import { Input } from "baseui/input";
import { Block } from "baseui/block";
import forge from "node-forge";

function TLOXP({ dispatch }) {
  const [css] = useStyletron();
  const [ssn, setSsn] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [leadId, setLeadId] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [apiResponse, setApiResponse] = useState(null); // State for API response
  const apiUrl = process.env.REACT_APP_API_URL;
  const printRef = useRef();

  const publicKeyPem = `-----BEGIN PUBLIC KEY-----
  MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAvpbN16+mUJjcMeCIvYZ3
  vm4xwAqZ2svo2jattzBDO8Nsm6seAJLyUaBs7brDP5YJlr16wY79Ia4uwMrKs8bt
  olnQtn8VXfHLQonnH/xqgkD+WNgwH1WKF8WnnB0xpILLV61aSsmOPKi4jmtbwHiR
  u750rYnSSXp2+iQxVdtkDNOYWr0HLdbQOOaZqsM7hoHETQ4HUgDpv7nT0quK6ad/
  /iQhz+AT4GHo9vcfUYQhlO4fYRJzoi1vpIOGXNvLrzRdu/Sws+D4kTT83TF3jYrU
  ktjl7DZsCRGc7fGzL7+L5/kiQp0+H+KVF11uFYGJLvQ35iEEDXPKzYirnZooRMuC
  PwIDAQAB
  -----END PUBLIC KEY-----`;

  const encryptSSN = (ssn) => {
    const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
    const encrypted = publicKey.encrypt(ssn, "RSA-OAEP", {
      md: forge.md.sha256.create(),
      mgf1: forge.mgf.mgf1.create(),
    });
    return forge.util.encode64(encrypted);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage(null);
    setApiResponse(null); // Clear previous response

    const encryptedSsn = encryptSSN(ssn);

    const userData = {
      ssn: encryptedSsn,
      leadId,
      dobYear,
      dobMonth,
      dobDay,
    };

    try {
      const response = await axios.post(`${apiUrl}/api/tlo/lookup`, userData);
      if (response.data.success) {
        setApiResponse(response.data.data); // Set API response
      } else {
        setErrorMessage(response.data.message || "Lookup failed.");
      }
    } catch (error) {
      setErrorMessage("Lookup failed");
    }

    setSsn("");
    setDobYear("");
    setDobMonth("");
    setDobDay("");
    setLeadId("");
  };

  const parseXML = (xmlString) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");
    return xmlDoc;
  };

  const namespaceResolver = (prefix) => {
    const namespaces = {
      soap: "http://schemas.xmlsoap.org/soap/envelope/",
      tlo: "http://tlo.com/",
    };
    return namespaces[prefix] || null;
  };

  const getNodeTextContent = (node, xpath, namespaceResolver) => {
    const result = document.evaluate(
      xpath,
      node,
      namespaceResolver,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    );
    return result.singleNodeValue ? result.singleNodeValue.textContent : "N/A";
  };

  const renderStructuredData = (data, title) => {
    const xmlDoc = parseXML(data);

    const verifyPlusResult = xmlDoc.evaluate(
      "//tlo:VerifyPlusResult",
      xmlDoc,
      namespaceResolver,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue;
    const personSearchResult = xmlDoc.evaluate(
      "//tlo:PersonSearchResult",
      xmlDoc,
      namespaceResolver,
      XPathResult.FIRST_ORDERED_NODE_TYPE,
      null
    ).singleNodeValue;

    console.log("verifyPlusResult:", verifyPlusResult);
    console.log("personSearchResult:", personSearchResult);

    if (!verifyPlusResult && !personSearchResult) {
      return <div>No results found.</div>;
    }

    return (
      <div className="print-area">
        <h4>{title}:</h4>
        {verifyPlusResult && (
          <div>
            <h5>VerifyPlus Result:</h5>
            <p>
              <strong>Name:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:FirstName",
                namespaceResolver
              )}{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:MiddleName",
                namespaceResolver
              )}{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:LastName",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>Total Score:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:TotalScore",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>SSN Score:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:SSNScore",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>Name Score:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:NameScore",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>Address Score:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:AddressScore",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>DOB Score:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:DateOfBirthScore",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>Full SSN Matches:</strong>{" "}
              {getNodeTextContent(
                verifyPlusResult,
                ".//tlo:FullSSNMatches",
                namespaceResolver
              )}
            </p>
          </div>
        )}
        {personSearchResult && (
          <div>
            <h5>PersonSearch Result:</h5>
            <p>
              <strong>Transaction ID:</strong>{" "}
              {getNodeTextContent(
                personSearchResult,
                ".//tlo:TransactionId",
                namespaceResolver
              )}
            </p>
            <p>
              <strong>Full Name:</strong>{" "}
              {getNodeTextContent(
                personSearchResult,
                ".//tlo:FirstName",
                namespaceResolver
              )}{" "}
              {getNodeTextContent(
                personSearchResult,
                ".//tlo:MiddleName",
                namespaceResolver
              )}{" "}
              {getNodeTextContent(
                personSearchResult,
                ".//tlo:LastName",
                namespaceResolver
              )}
            </p>
          </div>
        )}
      </div>
    );
  };
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className={css({ height: "100vh" })}>
      <Grid>
        <Cell span={12}>
          <Card>
            <StyledBody>
              <h2 style={{ textAlign: "center" }}>TLOXP LookUp</h2>
              <form onSubmit={handleSubmit}>
                <Block marginBottom="scale600">
                  <label htmlFor="ssn">Social Security Number</label>
                  <Input
                    id="ssn"
                    type="text"
                    value={ssn}
                    onChange={(e) => setSsn(e.target.value)}
                    placeholder="Enter Social Security Number"
                  />
                </Block>
                <Block marginBottom="scale600">
                  <label htmlFor="leadId">Lead ID</label>
                  <Input
                    id="leadId"
                    type="text"
                    value={leadId}
                    onChange={(e) => setLeadId(e.target.value)}
                    placeholder="Enter Lead ID"
                  />
                </Block>
                <Block marginBottom="scale600">
                  <label htmlFor="dobYear">DOB Year</label>
                  <Input
                    id="dobYear"
                    type="text"
                    value={dobYear}
                    onChange={(e) => setDobYear(e.target.value)}
                    placeholder="Enter DOB Year"
                  />
                </Block>
                <Block marginBottom="scale600">
                  <label htmlFor="dobMonth">DOB Month</label>
                  <Input
                    id="dobMonth"
                    type="text"
                    value={dobMonth}
                    onChange={(e) => setDobMonth(e.target.value)}
                    placeholder="Enter DOB Month"
                  />
                </Block>
                <Block marginBottom="scale600">
                  <label htmlFor="dobDay">DOB Day</label>
                  <Input
                    id="dobDay"
                    type="text"
                    value={dobDay}
                    onChange={(e) => setDobDay(e.target.value)}
                    placeholder="Enter DOB Day"
                  />
                </Block>

                <Button
                  type="submit"
                  overrides={{ BaseButton: { style: { width: "100%" } } }}
                >
                  Lookup
                </Button>
                {errorMessage && (
                  <div style={{ color: "red", textAlign: "center" }}>
                    {errorMessage}
                  </div>
                )}
              </form>
              {apiResponse && (
                <div ref={printRef} style={{ marginTop: "20px" }}>
                  <h3>Lookup Results:</h3>
                  {renderStructuredData(apiResponse.verifyPlus, "VerifyPlus")}
                  {renderStructuredData(
                    apiResponse.personSearch,
                    "PersonSearch"
                  )}
                </div>
              )}

              {apiResponse && (
                <Button
                  onClick={handlePrint}
                  overrides={{ BaseButton: { style: { width: "100%" } } }}
                >
                  Print
                </Button>
              )}
            </StyledBody>
          </Card>
        </Cell>
      </Grid>

      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-area, .print-area * {
              visibility: visible;
            }
            .print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
              box-sizing: border-box;
            }
          }
        `}
      </style>
    </div>
  );
}

export default TLOXP;
